import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import UserAvatar from '@/components/feature/UserAvatar';
import CategoryBadge from '@/components/feature/CategoryBadge';
import ReplySection from './components/ReplySection';
import RelatedTopics from './components/RelatedTopics';
import { getRelatedTopics, getRepliesByTopic, getTopicById, getUserById, withMeta } from '@/lib/community';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn, formatCount, formatDate } from '@/lib/format';

export default function TopicPage() {
  const { topicId = '' } = useParams();
  const rawTopic = getTopicById(topicId);
  const topic = rawTopic ? withMeta(rawTopic) : undefined;
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState('');

  usePageMeta({
    title: topic ? `${topic.title} - 蜜糖社区话题详情` : '话题未找到 - 蜜糖社区',
    description: topic
      ? `${topic.summary} 来自蜜糖社区「${topic.category.name}」分类，作者 ${topic.author.name}。${topic.tags.join('、')}`
      : '该话题不存在或已被删除，返回蜜糖社区继续浏览热门讨论。',
    keywords: topic ? `${topic.title},${topic.tags.join(',')},蜜糖社区,${topic.category.name}` : '蜜糖社区,话题详情',
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2600);
  };

  if (!topic) {
    return (
      <Layout>
        <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-16">
          <EmptyState
            icon="ri-error-warning-line"
            title="没有找到这个话题"
            description="它可能已被删除或链接有误，回到首页看看其他热门讨论吧。"
          />
          <div className="mt-6 flex justify-center">
            <Link to="/" className="rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const replies = getRepliesByTopic(topic.id);
  const related = getRelatedTopics(rawTopic!, 5);

  return (
    <Layout>
      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: topic.category.name, to: `/category/${topic.category.id}` },
            { label: topic.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Main */}
          <article className="lg:col-span-2">
            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              {topic.cover && (
                <div className="w-full h-56 md:h-72 bg-background-200">
                  <img src={topic.cover} alt={topic.title} title={`${topic.title} - 蜜糖社区话题封面`} className="w-full h-full object-top" />
                </div>
              )}

              <div className="p-5 md:p-7">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <CategoryBadge name={topic.category.name} color={topic.category.color} icon={topic.category.icon} />
                  {topic.isHot && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 text-accent-700 border border-accent-200 px-2 py-1 text-xs font-label font-bold">
                      <i className="ri-fire-fill text-[13px]" />
                      热门话题
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-900 leading-snug">{topic.title}</h1>

                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Link to={`/user/${topic.author.id}`} className="cursor-pointer">
                      <UserAvatar src={topic.author.avatar} alt={topic.author.name} size="md" />
                    </Link>
                    <div>
                      <Link to={`/user/${topic.author.id}`} className="text-sm font-bold text-foreground-900 hover:text-primary-700 transition-colors cursor-pointer">
                        {topic.author.name}
                      </Link>
                      <p className="text-xs text-foreground-400 mt-0.5">
                        发布于 {formatDate(topic.createdAt)} · {formatCount(topic.viewCount)} 次浏览
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setFavorited((v) => !v);
                      showToast(favorited ? '已取消收藏（前端演示）' : '收藏成功（前端演示）');
                    }}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer',
                      favorited
                        ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700',
                    )}
                  >
                    <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                    {favorited ? '已收藏' : '收藏'}
                  </button>
                </div>

                <div className="mt-6 rich-content text-[15px] text-foreground-700" dangerouslySetInnerHTML={{ __html: topic.content }} />

                <div className="mt-6 flex flex-wrap gap-2">
                  {topic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-secondary-100 text-secondary-800 px-3 py-1.5 text-xs font-label font-bold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <p className="mt-6 rounded-md bg-background-100 border border-background-200 px-4 py-3 text-xs text-foreground-500 flex items-start gap-2">
                  <i className="ri-information-line mt-0.5" />
                  本站为前端演示站点，收藏与回复功能仅作交互展示，不会保存到服务器。
                </p>
              </div>
            </div>

            <ReplySection topicId={topic.id} initialReplies={replies} onToast={showToast} />
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1 flex flex-col gap-5">
            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="text-base font-extrabold text-foreground-900 mb-4">关于作者</h2>
              <div className="flex items-center gap-3">
                <UserAvatar src={topic.author.avatar} alt={topic.author.name} size="lg" />
                <div className="min-w-0">
                  <p className="text-base font-bold text-foreground-900">{topic.author.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {topic.author.badges.map((badge) => (
                      <span key={badge} className="rounded-full bg-primary-100 text-primary-800 px-2 py-0.5 text-[11px] font-label font-bold">
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-3">{topic.author.bio}</p>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-background-100 py-2">
                  <p className="text-sm font-extrabold text-foreground-900">{topic.author.postCount}</p>
                  <p className="text-[11px] text-foreground-400">发帖</p>
                </div>
                <div className="rounded-md bg-background-100 py-2">
                  <p className="text-sm font-extrabold text-foreground-900">{formatCount(topic.author.followers)}</p>
                  <p className="text-[11px] text-foreground-400">粉丝</p>
                </div>
                <div className="rounded-md bg-background-100 py-2">
                  <p className="text-sm font-extrabold text-foreground-900">{formatCount(topic.author.replyCount)}</p>
                  <p className="text-[11px] text-foreground-400">回复</p>
                </div>
              </div>
              <Link
                to={`/user/${topic.author.id}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-bold px-4 py-2 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-3-line" />
                查看主页
              </Link>
            </section>

            <RelatedTopics topics={related} />

            <section className="bg-primary-100 border border-primary-200 rounded-lg p-5">
              <h2 className="text-base font-extrabold text-foreground-900">也有想法想分享？</h2>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">把你最近的心得写成话题，说不定能帮到正在找答案的人。</p>
              <Link
                to="/post"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-add-line" />
                发布话题
              </Link>
            </section>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-up">
          <div className="flex items-center gap-2 rounded-md bg-foreground-950 text-background-50 px-4 py-3 text-sm font-label shadow-sm">
            <i className="ri-checkbox-circle-fill text-primary-400" />
            {toast}
          </div>
        </div>
      )}
    </Layout>
  );
}